import { useState } from 'react';
import { useLang } from '../../../hooks/useLang';
import { getLocalizedNews, getLocalizedConferences, LocalizedNewsItem, LocalizedConference } from '../../../mocks/newsLocalized';

// AI-style key points extracted per news item (mock, keyed by index in allNews)
const AI_SUMMARIES: Record<number, string[]> = {
  0: ['监管层面重大信号，政策窗口期明确', '持牌机构合规要求进一步细化', '对跨境RWA发行具有直接指导意义'],
  1: ['机构资金大规模入场，市场信心增强', '代币化资产规模突破新高', '香港作为亚洲RWA中心地位进一步巩固'],
  2: ['内地企业跨境发行路径更加清晰', '监管沙盒试点范围扩大', '合规成本有望显著降低'],
  3: ['全球RWA标准化进程加速', '多国监管框架趋于统一', '跨境互认机制取得实质性进展'],
  4: ['技术基础设施持续完善', '链上结算效率大幅提升', '机构级托管解决方案日趋成熟'],
  5: ['碳资产代币化迎来政策利好', '绿色金融与RWA深度融合', '企业ESG合规路径拓宽'],
};

// Legacy Chinese news data (kept for zh/en tabs)
type NewsTab = 'local' | 'regional' | 'global';

const NEWS_TAB_LABELS: Record<string, Record<NewsTab, string>> = {
  zh: { local: '🇭🇰 香港资讯', regional: '🇨🇳 内地资讯', global: '🌐 国际资讯' },
  en: { local: '🇬🇧 UK & US', regional: '🇸🇬 Asia-Pacific', global: '🌐 Global' },
  fr: { local: '🇫🇷 France', regional: '🇪🇺 Europe', global: '🌐 Mondial' },
  es: { local: '🇪🇸 España', regional: '🇲🇽 Latinoamérica', global: '🌐 Global' },
  pt: { local: '🇧🇷 Brasil', regional: '🇵🇹 Portugal', global: '🌐 Global' },
  ja: { local: '🇯🇵 日本', regional: '🇸🇬 アジア太平洋', global: '🌐 グローバル' },
  de: { local: '🇩🇪 Deutschland', regional: '🇪🇺 Europa', global: '🌐 Global' },
  ko: { local: '🇰🇷 한국', regional: '🇸🇬 아시아태평양', global: '🌐 글로벌' },
  ar: { local: '🇦🇪 الإمارات', regional: '🇸🇦 الخليج', global: '🌐 عالمي' },
  ru: { local: '🇷🇺 Россия', regional: '🇨🇳 Азия', global: '🌐 Глобально' },
};

const SECTION_LABELS: Record<string, Record<string, string>> = {
  badge: { zh: 'RWA 行业资讯', en: 'RWA Industry News', fr: 'Actualités RWA', es: 'Noticias RWA', pt: 'Notícias RWA', ja: 'RWA業界ニュース', de: 'RWA-Branchennachrichten', ko: 'RWA 업계 뉴스', ar: 'أخبار صناعة RWA', ru: 'Новости Отрасли RWA' },
  title: { zh: '实时追踪全球RWA动态', en: 'Real-Time Global RWA Tracking', fr: 'Suivi Mondial RWA en Temps Réel', es: 'Seguimiento Global RWA en Tiempo Real', pt: 'Rastreamento Global RWA em Tempo Real', ja: 'グローバルRWAリアルタイム追跡', de: 'Globales RWA-Echtzeit-Tracking', ko: '글로벌 RWA 실시간 추적', ar: 'تتبع RWA العالمي في الوقت الفعلي', ru: 'Глобальное Отслеживание RWA в Реальном Времени' },
  subtitle: { zh: '精选权威机构最新政策与市场资讯', en: 'Curated latest policies and market news from authoritative institutions', fr: 'Dernières politiques et actualités de marché des institutions de référence', es: 'Últimas políticas y noticias de mercado de instituciones de referencia', pt: 'Últimas políticas e notícias de mercado de instituições de referência', ja: '権威ある機関からの最新政策と市場ニュース', de: 'Neueste Richtlinien und Marktnachrichten von Referenzinstitutionen', ko: '권위 있는 기관의 최신 정책 및 시장 뉴스', ar: 'أحدث السياسات وأخبار السوق من المؤسسات المرجعية', ru: 'Последние политики и рыночные новости от авторитетных институтов' },
  dailyUpdate: { zh: '每日更新', en: 'Daily Updates', fr: 'Mises à Jour Quotidiennes', es: 'Actualizaciones Diarias', pt: 'Atualizações Diárias', ja: '毎日更新', de: 'Tägliche Updates', ko: '매일 업데이트', ar: 'تحديثات يومية', ru: 'Ежедневные Обновления' },
  hot: { zh: '热点', en: 'HOT', fr: 'CHAUD', es: 'CALIENTE', pt: 'QUENTE', ja: '注目', de: 'HEISS', ko: '인기', ar: 'ساخن', ru: 'ГОРЯЧО' },
  latest: { zh: '最新', en: 'LATEST', fr: 'RÉCENT', es: 'ÚLTIMO', pt: 'RECENTE', ja: '最新', de: 'NEU', ko: '최신', ar: 'أحدث', ru: 'НОВОЕ' },
  sources: { zh: '资讯来源', en: 'News Sources', fr: 'Sources', es: 'Fuentes', pt: 'Fontes', ja: 'ニュースソース', de: 'Quellen', ko: '뉴스 소스', ar: 'مصادر الأخبار', ru: 'Источники Новостей' },
  disclaimer: { zh: '以上资讯由编辑团队从公开权威渠道整理，仅供参考学习。', en: 'News curated by editorial team from public authoritative sources for reference only.', fr: 'Actualités compilées par l\'équipe éditoriale à partir de sources publiques autorisées, à titre de référence uniquement.', es: 'Noticias recopiladas por el equipo editorial de fuentes públicas autorizadas, solo para referencia.', pt: 'Notícias compiladas pela equipe editorial de fontes públicas autorizadas, apenas para referência.', ja: '編集チームが公開権威ある情報源からまとめたニュースで、参考のみです。', de: 'Nachrichten vom Redaktionsteam aus öffentlichen Quellen zusammengestellt, nur zur Referenz.', ko: '편집팀이 공개 권위 있는 출처에서 수집한 뉴스로, 참고용으로만 제공됩니다.', ar: 'أخبار جمعها الفريق التحريري من مصادر عامة موثوقة، للمرجعية فقط.', ru: 'Новости, собранные редакционной командой из публичных авторитетных источников, только для справки.' },
  statsTitle: { zh: '资讯数据统计', en: 'News Statistics', fr: 'Statistiques', es: 'Estadísticas', pt: 'Estatísticas', ja: 'ニュース統計', de: 'Statistiken', ko: '뉴스 통계', ar: 'إحصائيات الأخبار', ru: 'Статистика Новостей' },
  todayUpdates: { zh: '今日更新', en: 'Today Updates', fr: "Aujourd'hui", es: 'Hoy', pt: 'Hoje', ja: '今日の更新', de: 'Heute', ko: '오늘 업데이트', ar: 'تحديثات اليوم', ru: 'Сегодня' },
  thisWeek: { zh: '本周发布', en: 'This Week', fr: 'Cette Semaine', es: 'Esta Semana', pt: 'Esta Semana', ja: '今週', de: 'Diese Woche', ko: '이번 주', ar: 'هذا الأسبوع', ru: 'На Этой Неделе' },
  institutions: { zh: '覆盖机构', en: 'Institutions', fr: 'Institutions', es: 'Instituciones', pt: 'Instituições', ja: '機関', de: 'Institutionen', ko: '기관', ar: 'مؤسسات', ru: 'Институты' },
  mediaTracked: { zh: '追踪媒体', en: 'Media Tracked', fr: 'Médias Suivis', es: 'Medios Seguidos', pt: 'Mídia Rastreada', ja: '追跡メディア', de: 'Verfolgte Medien', ko: '추적 미디어', ar: 'وسائل الإعلام المتابعة', ru: 'Отслеживаемые СМИ' },
  conferences: { zh: '近期会议', en: 'Upcoming Conferences', fr: 'Conférences à Venir', es: 'Próximas Conferencias', pt: 'Próximas Conferências', ja: '今後のカンファレンス', de: 'Bevorstehende Konferenzen', ko: '예정된 컨퍼런스', ar: 'المؤتمرات القادمة', ru: 'Предстоящие Конференции' },
};

function getL(key: string, lang: string): string {
  return SECTION_LABELS[key]?.[lang] ?? SECTION_LABELS[key]?.['en'] ?? key;
}

export default function RWANewsFeed() {
  const { lang } = useLang();
  const [activeTab, setActiveTab] = useState<NewsTab>('local');

  const allNews = getLocalizedNews(lang);
  const conferences = getLocalizedConferences(lang);
  const tabLabels = NEWS_TAB_LABELS[lang] ?? NEWS_TAB_LABELS['en'];

  // Split news into 3 tabs: first 2 items = local, next 2 = regional, rest = global
  const localNews = allNews.slice(0, 2);
  const regionalNews = allNews.slice(2, 4);
  const globalNews = allNews.slice(4);

  const newsMap: Record<NewsTab, LocalizedNewsItem[]> = {
    local: localNews,
    regional: regionalNews,
    global: globalNews,
  };

  const currentNews = newsMap[activeTab];

  // Map current tab news to global indices for AI summaries
  const tabOffset = activeTab === 'local' ? 0 : activeTab === 'regional' ? 2 : 4;

  return (
    <section id="news-feed" className="py-16 bg-[#F8F5FF] dark:bg-gray-900">
      <div className="max-w-[1440px] mx-auto px-4 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1 h-6 bg-[#7C3AED] rounded-full" />
              <span className="text-[#7C3AED] text-xs font-black tracking-widest uppercase">
                {getL('badge', lang)}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white">
              {getL('title', lang)}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              {getL('subtitle', lang)}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs text-gray-500 dark:text-gray-400">{getL('dailyUpdate', lang)}</span>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-1 mb-6 bg-white dark:bg-gray-800 p-1 rounded-xl w-fit border border-gray-100 dark:border-gray-700 shadow-sm">
          {(Object.keys(tabLabels) as NewsTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-2 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab ? 'bg-[#7C3AED] text-white shadow-md' : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {tabLabels[tab]}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* News List */}
          <div className="lg:col-span-2 space-y-4">
            {currentNews.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-8 text-center text-gray-400">
                <i className="ri-newspaper-line text-4xl mb-2 block text-gray-200"></i>
                <p className="text-sm">{getL('dailyUpdate', lang)}</p>
              </div>
            ) : currentNews.map((item, idx) => (
              <NewsCard
                key={item.id}
                item={item}
                idx={idx}
                lang={lang}
                hotLabel={getL('hot', lang)}
                latestLabel={getL('latest', lang)}
                aiSummary={AI_SUMMARIES[tabOffset + idx]}
              />
            ))}
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            {/* Conferences */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5">
              <h4 className="font-black text-gray-900 dark:text-white text-sm mb-4 flex items-center gap-2">
                <i className="ri-calendar-event-line text-[#7C3AED]" />
                {getL('conferences', lang)}
              </h4>
              <div className="space-y-3">
                {conferences.slice(0, 3).map((conf) => (
                  <ConferenceCard key={conf.id} conf={conf} />
                ))}
              </div>
            </div>

            {/* Disclaimer */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <i className="ri-information-line text-amber-600 mt-0.5 shrink-0" />
                <div className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
                  {getL('disclaimer', lang)}
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="bg-gradient-to-br from-[#2E1065] to-[#4C1D95] rounded-xl p-5 text-white">
              <h4 className="font-black text-sm mb-4">{getL('statsTitle', lang)}</h4>
              <div className="space-y-3">
                {[
                  { label: getL('todayUpdates', lang), value: '6' },
                  { label: getL('thisWeek', lang), value: '32' },
                  { label: getL('institutions', lang), value: '20' },
                  { label: getL('mediaTracked', lang), value: '38' },
                ].map((stat) => (
                  <div key={stat.label} className="flex items-center justify-between">
                    <span className="text-white/60 text-xs">{stat.label}</span>
                    <span className="font-black text-[#F59E0B] text-base">{stat.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

interface NewsCardProps {
  item: LocalizedNewsItem;
  idx: number;
  lang: string;
  hotLabel: string;
  latestLabel: string;
  aiSummary?: string[];
}

function NewsCard({ item, idx, lang, hotLabel, latestLabel, aiSummary }: NewsCardProps) {
  const [showAI, setShowAI] = useState(false);

  const categoryColors: Record<string, string> = {
    '监管政策': '#7C3AED', 'Regulation': '#7C3AED', 'Réglementation': '#7C3AED', 'Regulación': '#7C3AED',
    '产业动态': '#059669', 'Institutional': '#059669', 'Banque': '#059669', 'Banca': '#059669',
    '央行动态': '#DC2626', 'Technology': '#DC2626', 'Technologie': '#DC2626',
    '机构动态': '#0EA5E9', 'Market': '#0EA5E9', 'Marché': '#0EA5E9',
    '行业峰会': '#F59E0B', 'Event': '#F59E0B', 'Événement': '#F59E0B', 'Evento': '#F59E0B',
    '绿色RWA': '#059669', 'Carbon': '#059669', 'Carbono': '#059669',
  };
  const color = categoryColors[item.category] ?? '#7C3AED';

  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5 hover:border-[#7C3AED]/30 transition-all group">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-white mt-0.5" style={{ background: color }}>
          <i className="ri-newspaper-line text-base" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="text-xs font-bold text-gray-500 dark:text-gray-400">{item.sourceCountry} {item.source}</span>
            <span className="text-gray-300 dark:text-gray-600">·</span>
            <span className="text-xs text-gray-400">{item.date}</span>
            {item.hot && (
              <span className="px-1.5 py-0.5 bg-red-50 dark:bg-red-900/30 text-red-500 text-[10px] font-bold rounded border border-red-100 dark:border-red-800/40">
                🔥 {hotLabel}
              </span>
            )}
            {idx === 0 && (
              <span className="px-1.5 py-0.5 bg-[#7C3AED]/10 text-[#7C3AED] text-[10px] font-bold rounded">
                {latestLabel}
              </span>
            )}
          </div>
          <h3 className="font-bold text-gray-900 dark:text-white text-sm mb-2 group-hover:text-[#7C3AED] transition-colors">
            {item.title}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 text-xs leading-relaxed line-clamp-3">
            {item.summary}
          </p>

          {/* AI Summary Toggle */}
          {aiSummary && (
            <div className="mt-3">
              <button
                onClick={() => setShowAI(!showAI)}
                className="flex items-center gap-1.5 text-[10px] font-bold cursor-pointer transition-colors px-2.5 py-1 rounded-full border"
                style={showAI
                  ? { background: '#7C3AED', color: 'white', borderColor: '#7C3AED' }
                  : { background: '#7C3AED08', color: '#7C3AED', borderColor: '#7C3AED25' }
                }
              >
                <i className="ri-sparkling-line"></i>
                AI 精华摘要
                {showAI ? <i className="ri-arrow-up-s-line"></i> : <i className="ri-arrow-down-s-line"></i>}
              </button>

              {showAI && (
                <div className="mt-2.5 p-3 rounded-xl border border-[#7C3AED]/15 bg-gradient-to-br from-[#7C3AED]/5 to-[#A78BFA]/5 dark:from-[#7C3AED]/10 dark:to-[#A78BFA]/10">
                  <div className="flex items-center gap-1.5 mb-2">
                    <div className="w-4 h-4 flex items-center justify-center rounded bg-[#7C3AED] shrink-0">
                      <i className="ri-sparkling-line text-white text-[9px]"></i>
                    </div>
                    <span className="text-[10px] font-black text-[#7C3AED] tracking-wide">AI 核心要点提炼</span>
                    <span className="text-[9px] text-gray-400 ml-auto">仅供参考</span>
                  </div>
                  <ul className="space-y-1.5">
                    {aiSummary.map((point, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="w-4 h-4 flex items-center justify-center rounded-full bg-[#7C3AED]/15 text-[#7C3AED] text-[9px] font-black shrink-0 mt-0.5">{i + 1}</span>
                        <span className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 mt-3">
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium text-white" style={{ background: color }}>
              {item.category}
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}

function ConferenceCard({ conf }: { conf: LocalizedConference }) {
  const typeColors: Record<string, string> = {
    conference: '#7C3AED',
    summit: '#DC2626',
    forum: '#059669',
    webinar: '#0EA5E9',
  };
  const color = typeColors[conf.type] ?? '#7C3AED';

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-white text-xs font-black" style={{ background: color }}>
        <i className="ri-calendar-2-line text-sm"></i>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-bold text-gray-800 dark:text-gray-200 line-clamp-2 leading-snug">{conf.name}</p>
        <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
          <i className="ri-map-pin-line"></i>{conf.location} · {conf.date}
        </p>
      </div>
    </div>
  );
}
